import React, { useCallback, useEffect } from 'react';

import { ICONS, ICON_TYPES } from '@/src/components/common/Elements/Icon/Icon';
import useConfirmPopup from '@/src/components/common/Elements/Modal/useConfirmPopup';
import { showToast } from '@/src/globalFunction';

import { useConfirmDeletedEntry } from './useConfirmDeletedEntry';

import { ERROR_CODES, ResponseError } from '../../api/config';
import { invalidateInboxQueries } from '../../api/inbox';
import { useInbox } from '../../store/inbox.store';
import { useInboxEntryPanel } from '../../store/inboxEntryPanel.store';

type useValidateActionResponseProps = {
  responseError: ResponseError;
};

export const useValidateActionResponse = ({ responseError }: useValidateActionResponseProps) => {
  const [ConfirmRefreshDeletedModal, confirmRefreshDeleted] = useConfirmDeletedEntry();
  const [ConfirmRefreshStateChangedModal, confirmRefreshStateChanged] = useConfirmPopup({
    title: (
      <span className="tw-text-cfo-blue-600 tw-font-brandonTextMedium tw-text-[18px]">
        Changes have been made to this entry
      </span>
    ),
    message: `Please reload the page to see the latest updates before proceeding`,
    cancelBtn: {
      label: 'Cancel',
    },
    confirmBtn: {
      label: 'Reload the page',
    },
    icon: {
      icon: ICONS.info,
      iconType: ICON_TYPES.outlined,
      className: 'tw-text-cfo-blue-600 tw-text-[22px] tw-font-medium ',
    },
  });
  const { setInboxEntry } = useInboxEntryPanel();
  const { clearSocket } = useInbox();

  const handleValidateResponse = useCallback(
    async (response) => {
      if (!response) {
        // show default error
        showToast('error', 'Something went wrong please try again later.');
        return;
      }

      if (response.status === true) {
        return;
      }

      if (!response.error) {
        showToast('error', 'Something went wrong please try again later.');
      }

      if (response.error['error-code'] === ERROR_CODES.DELETED) {
        if (!(await confirmRefreshDeleted())) {
          return;
        }

        setInboxEntry(null);
      } else {
        if (!(await confirmRefreshStateChanged())) {
          return;
        }
      }

      invalidateInboxQueries();
      clearSocket();
    },
    [confirmRefreshDeleted, confirmRefreshStateChanged, setInboxEntry, clearSocket]
  );

  // We depend on the passed mutation error and trigger it via useEffect to centralize logic here
  useEffect(() => {
    if (!responseError) {
      return;
    }

    handleValidateResponse(responseError);
  }, [responseError, handleValidateResponse]);

  return {
    validateResponse: handleValidateResponse,
    confirmRefreshModal: (
      <React.Fragment>
        <ConfirmRefreshDeletedModal />
        <ConfirmRefreshStateChangedModal />
      </React.Fragment>
    ),
  };
};
